import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { DollarSignIcon } from '../../../../aida/components/svgs/icons/DollarSignIcon';
import { convertRawRichTextContentStateToRichContentEditorState } from '../../../../models/UiUtils';
import { brandPrimary, brandSecondary, noteTintColor, success, warningDark } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import { Attachments } from '../../Attachments';
import { DeprecatedPopover, PopoverType } from '../../DeprecatedPopover';
import { DefaultCompactEditorConfig } from '../../notes/NoteEditor';
import { RichContentDocumentEditor } from '../../richContent/RichContentDocumentEditor';
import { ActionItemsIcon } from '../../svgs/icons/ActionItemsIcon';
import { CalendarDateIcon } from '../../svgs/icons/CalendarDateIcon';
import { CallIcon } from '../../svgs/icons/CallIcon';
import { CheckmarkIcon } from '../../svgs/icons/CheckmarkIcon';
import { ClockIcon } from '../../svgs/icons/ClockIcon';
import { EmailIcon } from '../../svgs/icons/EmailIcon';
import { NotesIcon } from '../../svgs/icons/NotesIcon';
import { PostageIcon } from '../../svgs/icons/PostageIcon';
import { QuestionMarkIcon } from '../../svgs/icons/QuestionMarkIcon';
import { StarIcon } from '../../svgs/icons/StarIcon';
import { TextingIcon } from '../../svgs/icons/TextingIcon';
import { styleSheet } from './styles';

export interface IEntityTimelineEventBaseProps<
	TTimelineEvent extends Api.ITimelineEvent = Api.ITimelineEvent,
	T extends Api.TimelineEventViewModel<TTimelineEvent> = Api.TimelineEventViewModel<TTimelineEvent>,
> {
	event: T;
}

export const EntityTimelineEventIcon: React.FC<IEntityTimelineEventBaseProps> = ({ event }) => {
	switch (event.type) {
		case 'SentEmailEvent':
		case 'ReceivedEmailEvent':
		case 'ViewedEmailEvent':
		case 'RepliedEmailEvent':
		case 'ExternalSentEmailEvent':
		case 'HtmlNewsletterEvent':
			return <EmailIcon fillColor={brandPrimary} />;
		case 'MeetingEvent':
		case 'MeetingScheduledEvent':
			return <CalendarDateIcon fillColor={brandSecondary} />;
		case 'DealCreatedEvent':
		case 'DealUpdatedEvent':
			return <DollarSignIcon className={css(styleSheet.dollarSignIcon)} style={{ color: brandSecondary }} />;
		case 'NoteEvent':
		case 'SkipLeadEvent':
			return <NotesIcon fillColor={noteTintColor} />;
		case 'ActionItemEvent':
			return <ActionItemsIcon fillColor={success} />;
		case 'ConversationThreadEvent':
			return <TextingIcon fill={warningDark} />;
		case 'PhoneCallEvent':
		case 'PhoneCallCompletedEvent':
		case 'UntrackedPhoneCallEvent':
			return <CallIcon fillColor={success} />;
		case 'FollowUpEvent':
		case 'CancelledFollowUpEvent':
		case 'RescheduledFollowUpEvent':
			return <ClockIcon fillColor={brandSecondary} />;
		case 'SatisfactionSurveyResponseEvent':
			return (
				<StarIcon fillColor='#F3CE0D' filled={true} className={css(styleSheet.satisfactionSurveyResponseEventIcon)} />
			);
		case 'HandwrittenCardOrderEvent':
			return <PostageIcon fillColor={brandPrimary} />;
		default:
			return null;
	}
};

export const EntityTimelineEventDateHeader: React.FC<{ date: Date }> = ({ date }) => {
	return (
		<div className={css(styleSheet.eventDateContainer)}>
			<div className={css(styleSheet.eventDate)}>{moment(date).format('dddd, MMMM Do, YYYY')}</div>
			<div className={css(styleSheet.eventDateFillLine)} />
		</div>
	);
};

export interface IEntityTimelineEventActionProps extends IEntityTimelineEventBaseProps {
	menu?: React.ReactNode;
}

export const EntityTimelineEventAction: React.FC<IEntityTimelineEventActionProps> = observer(({ event, menu }) => {
	const [toggleShowingTooltip, setToggleShowingTooltip] = React.useState(false);

	if (event.type === 'SentEmailEvent' || event.type === 'HtmlNewsletterEvent') {
		if (!(event as Api.SentEmailEventViewModel)?.viewmodel?.rawContentState) {
			return <span className={css(styleSheet.sentOutSideOfLev)}>Sent outside of Levitate</span>;
		}

		// render email viewed
		const emailEvent = event as Api.SentEmailEventViewModel;
		const openDate = emailEvent.openDate;
		const status = (emailEvent.note?.context as Api.IEmailMessageSendContext)?.status;

		return openDate ? (
			<>
				<div className={css(baseStyleSheet.flex)}>
					<div className={css(styleSheet.viewedOn)}>
						<span>
							<CheckmarkIcon className={css(baseStyleSheet.absoluteCenter)} fillColor='#fff' />
						</span>
						Viewed on {moment(openDate).format('MM/DD/YYYY')}
					</div>
					{menu}
				</div>
				{status === Api.EmailTransactionStatus.MachineOpened ? (
					<div>
						<DeprecatedPopover
							className={css(styleSheet.popOver)}
							anchor={
								<div
									className={css(styleSheet.popOverIconWrap)}
									onMouseEnter={() => setToggleShowingTooltip(true)}
									onMouseLeave={() => setToggleShowingTooltip(false)}
								>
									<QuestionMarkIcon className={css(styleSheet.questionMarkIcon)} fillColor={brandPrimary} />
								</div>
							}
							contentClassName={css(styleSheet.popOverContentWrap)}
							dismissOnClickOutside={false}
							isOpen={toggleShowingTooltip}
							place='below'
							preferredPlacement='right'
							tipSize={0.01}
							type={PopoverType.gray}
						>
							<p className={css(styleSheet.popOverContent)}>
								This email was opened by an Apple device, we can&rsquo;t <br />
								determine if this contact has viewed the email.
							</p>
						</DeprecatedPopover>
					</div>
				) : null}
			</>
		) : (
			<div className={css(baseStyleSheet.flex)}>
				<div className={css(styleSheet.viewedOn)}>Email not viewed</div>
				{menu}
			</div>
		);
	} else if (event.type === 'NoteEvent' || event.type === 'ActionItemEvent' || event.type === 'PhoneCallEvent') {
		if (!(event as Api.NoteEventViewModel)?.viewmodel?.rawContentState) {
			return null;
		}
		return <>{menu}</>;
	}

	return null;
});

export const EntityTimelineEventTitle: React.FC<IEntityTimelineEventBaseProps> = ({ event }) => {
	let decoratedTitle: React.ReactNode = null;
	const hoverTitle: string = event.originalCreator ? `${event.title} created by ${event.originalCreator}` : event.title;

	/**
	 * External Associations Display Logic for Note Events
	 *
	 * BUSINESS REQUIREMENTS:
	 * 1. Display only ONE association to keep the UI clean and readable
	 * 2. Prioritize associations with timestamps (successful syncs with date info)
	 * 3. Alert users if ANY association failed, regardless of which one is displayed
	 *
	 * WHY THIS COMPLEXITY?
	 * - Users need to see successful sync information when available (timestamps are valuable)
	 * - But they also need to be alerted to any failures, even if from a different integration
	 * - Example: Redtail synced successfully (with date), Salesforce failed
	 *   → Show: "Synced with Redtail 07/29/25" + warning icon with Salesforce error
	 *
	 * This approach gives users both the most useful sync info AND failure awareness.
	 */
	if (event.type === 'NoteEvent') {
		const noteEvent = event as Api.NoteEventViewModel;
		const associations = noteEvent.viewmodel.externalAssociations;

		if (associations && associations.length > 0) {
			// DISPLAY LOGIC: Prioritize associations with timestamps (successful syncs with dates)
			// because this gives users the most valuable information about when data was last synced
			const associationToShow =
				associations.find((assoc: Api.IExternalAssociation) => assoc.timestamp) || associations[0];

			// FAILURE DETECTION: Check ALL associations for failures, not just the one we're displaying
			// This ensures users are alerted to ANY sync issues, even if we're showing a successful sync
			const hasFailure = associations.some((assoc: Api.IExternalAssociation) => assoc.failure);
			const failureMessage = associations.find((assoc: Api.IExternalAssociation) => assoc.failure)?.failure;

			const syncText = associationToShow.timestamp
				? ` - Synced with ${associationToShow.source} ${moment(associationToShow.timestamp).format('MM/DD/YY')}`
				: ` - Synced with ${associationToShow.source}`;

			decoratedTitle = (
				<div className={css(baseStyleSheet.truncateText)}>
					{event.title}
					<span className={css(styleSheet.externalAssociationInline)}>
						{syncText}
						{hasFailure && (
							<span className={css(styleSheet.errorIcon)} title={failureMessage}>
								⚠️
							</span>
						)}
					</span>
				</div>
			);
		}
	}

	if (event instanceof Api.SatisfactionSurveyResponseEventViewModel) {
		const surveyNameIndex = new RegExp(`${event.surveyName}`, 'igm').exec(event.title)?.index || -1;
		if (surveyNameIndex >= 0) {
			const ratingIndex = new RegExp(`${event.response.rating}$`, 'igm').exec(event.title)?.index || -1;

			let remainder = event.title.substring(surveyNameIndex + event.surveyName.length);
			if (ratingIndex >= 0) {
				remainder = remainder.substring(0, remainder.length - event.response.rating.toString().length);
			}
			decoratedTitle = (
				<>
					<span>{event.title.substring(0, surveyNameIndex)}</span>
					&nbsp;
					<span className={css(baseStyleSheet.truncateText, styleSheet.eventTitleSurveyName)} title={event.surveyName}>
						{`"${event.surveyName}"`}
					</span>
					&nbsp;
					{ratingIndex >= 0 ? (
						<span>
							{remainder}
							<span className={css(styleSheet.eventTitleSurveyRating)}>{event.response.rating}</span>
						</span>
					) : (
						<span>{remainder}</span>
					)}
				</>
			);
		}
	}
	return (
		<div className={`entity-title ${css(baseStyleSheet.truncateText, styleSheet.eventTitle)}`} title={hoverTitle}>
			{decoratedTitle || <div className={css(baseStyleSheet.truncateText)}>{event.title}</div>}
		</div>
	);
};

export const EntityTimelineSatisfactionSurveyResponseEventContent: React.FC<
	IEntityTimelineEventBaseProps<Api.ISatisfactionSurveyResponseEvent, Api.SatisfactionSurveyResponseEventViewModel>
> = ({ event }) => {
	const [expanded, setExpanded] = React.useState<boolean>(false);
	React.useEffect(() => {
		setExpanded(false);
	}, [event.response.feedback]);

	const feedback = React.useMemo<{
		expanded: string;
		collapsed?: string;
	}>(() => {
		const words = event.response.feedback?.split(' ');

		if (words?.length > 100) {
			return {
				collapsed: words.slice(0, 100).join(' '),
				expanded: event.response.feedback,
			};
		}
		return { expanded: event.response.feedback };
	}, [event.response.feedback]);

	if (!event.response.feedback) {
		return null;
	}

	const expand = () => setExpanded(true);

	return (
		<div className={css(styleSheet.satisfactionSurveyResponseEventContent)}>
			<div className={css(styleSheet.satisfactionSurveyResponseEventRow)}>
				<div className={css(styleSheet.satisfactionSurveyResponseEventContentLabel)}>Feedback:</div>
				&nbsp;
				{!feedback.collapsed || expanded ? (
					<div className={css(baseStyleSheet.breakWord, styleSheet.satisfactionSurveyResponseEventFeedback)}>
						{`"${feedback.expanded}"`}
					</div>
				) : (
					<div className={css(baseStyleSheet.breakWord)}>
						{`"${feedback.collapsed}..."`}
						{!expanded && (
							<>
								&nbsp;
								<button className={css(baseStyleSheet.brandLink)} onClick={expand}>
									Read more
								</button>
							</>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export const EntityTimelineNoteEventContent = ({ note }: { note: Api.NoteEventViewModel }) => {
	return (
		<div className={css(styleSheet.eventNoteContainer)}>
			<RichContentDocumentEditor
				autoFocus={false}
				className='note-editor-body-editor'
				config={DefaultCompactEditorConfig}
				contentState={convertRawRichTextContentStateToRichContentEditorState(note.viewmodel.rawContentState)}
				readOnly={true}
			/>
			{note.viewmodel.attachments ? <Attachments attachments={note.note.attachments} /> : null}
		</div>
	);
};
